/**
 * Redux DevTools Sanitization Utilities
 *
 * Sanitizes PCI-related information so sensitive data appears as "****"
 * when debugging state and actions in Redux DevTools.
 */

// Action types that should be sanitized
const SENSITIVE_ACTION_TYPES = [
  "payment/CREDIT_CARD_FORM_ACTION",
  "payment/ACH_FORM_ACTION"
];

// Field names used in redux-freeform actions (payload.payload.fieldName)
const SENSITIVE_FIELD_NAMES = [
  // Credit card fields
  "nameOnCard",
  "creditCardNumber",
  "cvv",
  "expirationDate",
  // ACH / Bank account fields
  "name",
  "routingNumber",
  "confirmRoutingNumber",
  "accountNumber",
  "confirmAccountNumber"
];

const MASKED_VALUE = "****";

/**
 * Deep sanitize an object by recognizing redux-freeform shape: { fieldName, value }
 * and masking the value based on fieldName.
 * @param {*} obj - The object to sanitize
 * @returns {*} Sanitized copy of the object
 */
const deepSanitize = obj => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitives
  if (typeof obj !== "object") {
    return obj;
  }

  // Handle arrays - recursively sanitize each item
  if (Array.isArray(obj)) {
    return obj.map(item => deepSanitize(item));
  }

  // Handle objects
  // Check if this is a redux-freeform object: { fieldName, value }
  if (
    Object.prototype.hasOwnProperty.call(obj, "fieldName") &&
    Object.prototype.hasOwnProperty.call(obj, "value") &&
    typeof obj.fieldName === "string"
  ) {
    const name = obj.fieldName;
    // If fieldName matches a sensitive field, mask the value
    if (SENSITIVE_FIELD_NAMES.includes(name)) {
      return { ...obj, value: obj.value ? MASKED_VALUE : obj.value };
    }
  }

  // For all other objects, recursively traverse to find nested { fieldName, value } pairs
  // This loop is necessary to reach deeply nested sensitive objects like:
  // { payload: { payload: { fieldName: "cvv", value: "123" } } }
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    sanitized[key] =
      typeof value === "object" && value !== null ? deepSanitize(value) : value;
  }
  return sanitized;
};

/**
 * Check if an action type matches a sensitive action pattern
 * @param {string} actionType - The action type to check
 * @returns {boolean} True if the action is sensitive
 */
const isSensitiveAction = actionType => {
  if (!actionType) return false;

  return SENSITIVE_ACTION_TYPES.some(sensitiveType =>
    actionType.includes(sensitiveType)
  );
};

/**
 * Sanitize Redux actions before they appear in DevTools
 * @param {Object} action - Redux action to sanitize
 * @returns {Object} Sanitized action
 */
export const actionSanitizer = action => {
  if (!action || !action.type) {
    return action;
  }

  // Check if this action type should be sanitized
  if (isSensitiveAction(action.type)) {
    return {
      ...action,
      payload: action.payload ? deepSanitize(action.payload) : action.payload
    };
  }

  return action;
};

/**
 * Sanitize Redux state before it appears in DevTools
 * @param {Object} state - Redux state to sanitize
 * @returns {Object} Sanitized state
 */
export const stateSanitizer = state => {
  if (!state || typeof state !== "object") {
    return state;
  }

  // Sanitize checkout state which contains payment forms
  if (state.checkout) {
    return {
      ...state,
      checkout: {
        ...state.checkout,
        forms: state.checkout.forms
          ? deepSanitize(state.checkout.forms)
          : state.checkout.forms
      }
    };
  }

  return state;
};
